import React, {
  PropsWithChildren,
  ReactElement,
  useCallback,
  useEffect,
} from 'react';
import { useDialog } from 'components/core/Dialog/common/DialogContext';
import { hasObjectIdChanged } from 'helpers/idComparison';
import { useLocation } from 'react-router-dom';
import {
  useAppUrlParams,
  UrlParamName,
} from 'hooks/navigation/useAppUrlParams';
import { changeConfirmationDialog } from 'views/projects/selection/selectionConfirmationDialog';
import { useProjectId } from 'store/project';
import { EntityOnProjectRoute, chooseEntity } from './model';
import { useProjects } from 'store/projects';

const WithProjectChangeDialog: React.FC<PropsWithChildren<{}>> =
  ({ children }) => {
    const selectedProjectId = useProjectId();
    const { items: projects } = useProjects();
    const createDialog = useDialog();
    const projectId = useAppUrlParams(UrlParamName.PROJECT_ID);
    const location = useLocation();

    const openSwitchProjectDialog = useCallback(
      (
        projectIdFromParams: string,
        projectName: string | undefined,
        entity: EntityOnProjectRoute
      ) => {
        changeConfirmationDialog(
          createDialog,
          projectIdFromParams,
          projectName,
          entity
        );
      },
      [createDialog]
    );

    useEffect(() => {
      if (!selectedProjectId) {
        return;
      }

      if (!projects.length) {
        return;
      }

      const foundProject = projects.find(
        (project) => project._id === projectId
      );

      if (
        foundProject &&
        hasObjectIdChanged(selectedProjectId, projectId)
      ) {
        const entity = chooseEntity(location);
        if (!entity) return;
        openSwitchProjectDialog(projectId, foundProject.name, entity);
      }
    }, [
      projectId,
      location,
      selectedProjectId,
      projects,
      openSwitchProjectDialog,
    ]);

    return <>{children}</>;
  };

const withProjectChangeDialog =
  (Component: React.ComponentType<any>) =>
  ({ ...props }): ReactElement => (
    <WithProjectChangeDialog>
      <Component {...props} />
    </WithProjectChangeDialog>
  );

export { withProjectChangeDialog };
